import type { RouteObject } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { canAccessSystemSettings, canAccessUserSearch } from "@/components/PortfolioSidebar/permissions";
import { RequireAuth } from "@/components/Routing/RequireAuth";
import { RequireFeatureFlag } from "@/components/Routing/RequireFeatureFlag";
import { RequirePortfolioAuth } from "@/components/Routing/RequirePortfolioAuth";
import { BookingAnalyticsPage } from "@/modules/analytics/pages/bookings/BookingAnalyticsPage";
import { CommunityFeedSentimentAnalyticsPage } from "@/modules/analytics/pages/community-feed-sentiment/CommunityFeedSentimentAnalyticsPage";
import { CommunityFeedAnalyticsPage } from "@/modules/analytics/pages/community-feed/CommunityFeedAnalyticsPage";
import { EventAnalyticsPage } from "@/modules/analytics/pages/events/EventAnalyticsPage";
import { AnalyticsExplorePage } from "@/modules/analytics/pages/explore/AnalyticsExplorePage";
import { TicketAnalyticsPage } from "@/modules/analytics/pages/tickets/TicketAnalyticsPage";
import { CreateProjectPage } from "@/modules/project/pages/create-or-edit/CreateProjectPage";

import { PortfolioAnalyticsScope } from "./components/PortfolioAnalyticsScope";
import { AddAdminPage } from "./pages/add-admin/AddAdminPage";
import { AvailableIntegrationDetailPage } from "./pages/available-integration-detail/AvailableIntegrationDetailPage";
import { BulkUploadAddressesPage } from "./pages/bulk-upload-addresses/BulkUploadAddressesPage";
import { PortfolioChatListPage } from "./pages/chat-list/PortfolioChatListPage";
import { CreateMassPostPage } from "./pages/create-mass-post/CreateMassPostPage";
import { CreateMassSurveyPage } from "./pages/create-mass-survey/CreateMassSurveyPage";
import { PortfolioOverviewLegacyPage } from "./pages/overview-legacy/PortfolioOverviewLegacyPage";
import { PortfolioOverviewPage } from "./pages/overview/PortfolioOverviewPage";
import { AiPortfolioIntegrationPage } from "./pages/portfolio-integrations/AiPortfolioIntegrationPage";
import { ILoqPortfolioIntegrationPage } from "./pages/portfolio-integrations/ILoqPortfolioIntegrationPage";
import { PortfolioIntegrationsPage } from "./pages/portfolio-integrations/PortfolioIntegrationsPage";
import { RemsPortfolioIntegrationPage } from "./pages/portfolio-integrations/RemsPortfolioIntegrationPage";
import { PortfolioTicketListPage } from "./pages/portfolio-ticket-list/PortfolioTicketListPage";
import { TicketSettingsPage } from "./pages/ticket-settings/TicketSettingsPage";
import { UsersLookupPage } from "./pages/users-lookup/UsersLookupPage";
import { portfolioRoutes } from "./routes";

export const portfolioRoute = [
  {
    path: portfolioRoutes.overview,
    element: <PortfolioOverviewLegacyPage />,
  },
  {
    path: portfolioRoutes.newProject,
    element: (
      <RequireAuth permission={canAccessSystemSettings}>
        <CreateProjectPage />
      </RequireAuth>
    ),
  },
  {
    path: portfolioRoutes.userLookup,
    element: (
      <RequireAuth permission={canAccessUserSearch}>
        <UsersLookupPage />
      </RequireAuth>
    ),
  },
  {
    path: portfolioRoutes.addAdmin,
    element: (
      <RequireFeatureFlag flag="isAdminMultiProjectsInviteAvailable">
        <RequirePortfolioAuth permission={(x) => x.canInviteAdmins}>
          <AddAdminPage />
        </RequirePortfolioAuth>
      </RequireFeatureFlag>
    ),
  },
  {
    path: portfolioRoutes.bulkUploadAddresses,
    element: (
      <RequireFeatureFlag flag="isPortfolioBulkUploadAddressAvailable">
        <RequirePortfolioAuth permission={(x) => x.canMassCreateAddresses}>
          <BulkUploadAddressesPage />
        </RequirePortfolioAuth>
      </RequireFeatureFlag>
    ),
  },
  { path: portfolioRoutes.createMassMessage, element: <CreateMassPostPage /> },
  { path: portfolioRoutes.createMassSurvey, element: <CreateMassSurveyPage /> },
  { path: portfolioRoutes.tickets, element: <PortfolioTicketListPage /> },
  { path: portfolioRoutes.ticketSettings, element: <TicketSettingsPage /> },
  { path: portfolioRoutes.chats, element: <PortfolioChatListPage /> },
  { path: portfolioRoutes.integrations, element: <PortfolioIntegrationsPage /> },
  {
    path: portfolioRoutes.manageAiIntegration,
    element: <AiPortfolioIntegrationPage />,
  },
  {
    path: portfolioRoutes.manageRemsIntegration,
    element: (
      <RequireAuth permission={canAccessSystemSettings}>
        <RemsPortfolioIntegrationPage />
      </RequireAuth>
    ),
  },
  {
    path: portfolioRoutes.manageILoqIntegration,
    element: (
      <RequireAuth permission={canAccessSystemSettings}>
        <ILoqPortfolioIntegrationPage />
      </RequireAuth>
    ),
  },
  { path: portfolioRoutes.integrationDetails, element: <AvailableIntegrationDetailPage /> },
  {
    path: portfolioRoutes.analyticsOverview,
    element: <PortfolioOverviewPage />,
  },
  {
    path: portfolioRoutes.analyticsCommunityFeed,
    element: (
      <PortfolioAnalyticsScope key="community-feed">
        <CommunityFeedAnalyticsPage />
      </PortfolioAnalyticsScope>
    ),
  },
  {
    path: portfolioRoutes.analyticsSentiment,
    element: (
      <PortfolioAnalyticsScope key="sentiment">
        <CommunityFeedSentimentAnalyticsPage />
      </PortfolioAnalyticsScope>
    ),
  },
  {
    path: portfolioRoutes.analyticsEvents,
    element: (
      <PortfolioAnalyticsScope key="events">
        <EventAnalyticsPage />
      </PortfolioAnalyticsScope>
    ),
  },
  {
    path: portfolioRoutes.analyticsTickets,
    element: (
      <PortfolioAnalyticsScope key="tickets">
        <TicketAnalyticsPage />
      </PortfolioAnalyticsScope>
    ),
  },
  {
    path: portfolioRoutes.analyticsBookings,
    element: (
      <PortfolioAnalyticsScope key="bookings">
        <BookingAnalyticsPage />
      </PortfolioAnalyticsScope>
    ),
  },
  {
    path: portfolioRoutes.analyticsExplore,
    element: <AnalyticsExplorePage scope="portfolio" />,
  },
  {
    path: portfolioRoutes.analyticsExploreEvents,
    element: (
      <PortfolioAnalyticsScope key="explore-events">
        <EventAnalyticsPage mode="all" />
      </PortfolioAnalyticsScope>
    ),
  },
  {
    path: portfolioRoutes.analyticsExploreBookings,
    element: (
      <PortfolioAnalyticsScope key="explore-bookings">
        <BookingAnalyticsPage mode="all" />
      </PortfolioAnalyticsScope>
    ),
  },
  {
    path: portfolioRoutes.analyticsExploreCommunityFeed,
    element: (
      <PortfolioAnalyticsScope key="explore-community-feed">
        <CommunityFeedAnalyticsPage mode="all" />
      </PortfolioAnalyticsScope>
    ),
  },
  {
    path: portfolioRoutes.analyticsExploreSentiment,
    element: (
      <PortfolioAnalyticsScope key="explore-sentiment">
        <CommunityFeedSentimentAnalyticsPage mode="all" />
      </PortfolioAnalyticsScope>
    ),
  },
  {
    path: portfolioRoutes.analyticsExploreTickets,
    element: (
      <PortfolioAnalyticsScope key="explore-tickets">
        <TicketAnalyticsPage mode="all" />
      </PortfolioAnalyticsScope>
    ),
  },
  { path: "*", element: <ErrorPage status={404} /> },
] satisfies RouteObject[];
